import { prop } from '@ipfabric/utils/fp/prop';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { selectAllowedRoutes } from 'components/GlobalState/selectors/selectAllowedRoutes';
import { selectMenuTree } from 'components/GlobalState/selectors/selectMenuTree';
import { useLocale } from 'containers/App/LocaleContext';
import { getRoutesWithApiEndpoint } from 'containers/App/routing/RoutesWithApiEndpoint';
import { record } from 'fp-ts';
import { useMemo } from 'react';
import { useCommandModel } from 'utils/hooks/useCommandModel';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { getApiTablePathsWithName } from './helpers/getApiTablePathsWithName';
import {
  type DeviceViewModel,
  createInitialDeviceViewState,
} from './model/DeviceViewModel';
import type { HandlersContainer } from './model/HandlersContainer';

interface DeviceViewProps {
  container?: HTMLElement | null;
  handlersContainer?: HandlersContainer;
}

const useDeviceViewContainer = (container?: HTMLElement | null) => {
  const { contentWrapper } = useGlobalStateContextSelectors({
    contentWrapper: prop('contentWrapper'),
  });
  return container === undefined ? contentWrapper : container;
};

export const useDeviceView = (props: DeviceViewProps = {}): DeviceViewModel => {
  const deviceViewContainer = useDeviceViewContainer(props.container);
  const { showNotification } = useNotifications();
  const msg = useLocale();
  const globalStateCtxProps = useGlobalStateContextSelectors({
    allowedRoutes: selectAllowedRoutes,
    menuTree: selectMenuTree,
    oas: prop('oas'),
  });
  const routesWithApiEndpoint = useMemo(
    () => getRoutesWithApiEndpoint(globalStateCtxProps.allowedRoutes, {}),
    [globalStateCtxProps.allowedRoutes],
  );
  const pathsWithName = useMemo(
    () =>
      getApiTablePathsWithName(routesWithApiEndpoint, globalStateCtxProps.oas),
    [routesWithApiEndpoint, globalStateCtxProps.oas],
  );

  return useCommandModel(
    createInitialDeviceViewState,
    {
      api: useApi(),
      deviceViewContainer,
      msg,
      showNotification,
    },
    {
      deviceViewContainer,
      handlersContainer: props.handlersContainer ?? record.empty,
      pathsWithName,
      routesWithApiEndpoint,
    },
  );
};
